<template lang="pug">
.annual-income
  h1 {{ $t("property_page.annual_income_graph") }}
  .chart-wrapper
    #annual-income-chart
</template>

<script>
import {api} from '@/api'
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import am4themes_animated from "@amcharts/amcharts4/themes/animated"
import { bus } from '@/bus.js'

export default {
  props: {
    property: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      data: [],
      chart: undefined,
      apiUrl: `properties/${this.property.id}/annual_incomes`,
      recommendedRent: {
        deposit: {
          value: 1
        },
        keyMoney: {
          value: 1
        }
      }
    }
  },
  computed: {
    maxValueOfIncomeFee(){
      return Math.max(...this.data.map((el) => el.income))
    },

    pageWidth() {
      return window.innerWidth
    }
  },
  mounted() {
    this.loadData()

    bus.$on('annualIncomeOpionsChange', (recommendedRentOptions) => {
      this.recommendedRent = recommendedRentOptions
      this.loadData()
    })
  },
  methods: {
    loadData() {
      const params = { recommendedRentOptions: this.recommendedRent }
      return api.get(this.apiUrl, { params }).then(({ data: { data }}) => {
        this.data = data
        this.chart = this.initChart()
        this.initCategoryAxis(this.chart)
        this.initValueAxis(this.chart)
        this.initSeries(this.chart)
      })
    },
    initChart(){
      am4core.useTheme(am4themes_animated)
      let chart = am4core.create("annual-income-chart", am4charts.XYChart)
      chart.data = this.data
      chart.svgContainer.htmlElement.style.height = "400px"
      chart.paddingLeft = -30
      chart.paddingTop = 60
      chart.paddingBottom = 63
      chart.paddingRight = 40
      chart.numberFormatter.numberFormat = "#a"
      chart.numberFormatter.bigNumberPrefixes = [
        { "number": 1e+4, "suffix": "" }
      ]
      return chart
    },
    initCategoryAxis(chart){
      let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis())
      categoryAxis.dataFields.category = "age"
      categoryAxis.paddingRight = 25
      categoryAxis.renderer.minGridDistance = 1
      categoryAxis.renderer.grid.template.strokeWidth = 0
      categoryAxis.title.text = this.$t("property_page.age")
      categoryAxis.title.align = "right"
      categoryAxis.title.dy = -16
      categoryAxis.title.dx = 60
      categoryAxis.paddingLeft = 22
      categoryAxis.title.fill = am4core.color("#3D4A57")
      categoryAxis.title.fontWeight = "bold"
      categoryAxis.renderer.labels.template.fill = am4core.color("#7C91A7")
      categoryAxis.fontSize = 13
      categoryAxis.renderer.labels.template.adapter.add("dy", function(dy, target) {
        if (target.dataItem && (target.dataItem.index + 1) % 5 > 0) {
          return dy + 1000
        }
        return dy + 13
      })
    },
    initValueAxis(chart) {
      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
      valueAxis.renderer.grid.template.strokeWidth = 1
      valueAxis.title.text = this.$t('property_page.annual_income_currency')
      valueAxis.title.fontWeight = "bold"
      valueAxis.renderer.minGridDistance = 30
      valueAxis.renderer.labels.template.fill = am4core.color("#7C91A7")
      valueAxis.title.rotation = 0
      valueAxis.title.valign = "top"
      valueAxis.renderer.labels.template.dx = -10
      valueAxis.title.dy = -50
      valueAxis.title.dx = 40
      valueAxis.title.fill = am4core.color("#3D4A57")
      valueAxis.fontSize = 13
      valueAxis.max = Math.round(this.maxValueOfIncomeFee * 1.3)
      valueAxis.min = 0
    },
    initSeries(chart) {
      let series = chart.series.push(new am4charts.ColumnSeries())
      series.name = "income"
      series.paddingLeft = 22
      series.columns.template.fillOpacity = 1
      series.dataFields.valueY = "income"
      series.dataFields.categoryX = "age"
      series.columns.template.fill = am4core.color("#008BD0")
      if ( this.pageWidth > 992) {
        series.columns.template.maxWidth = 10
      } else if (this.pageWidth < 992 && this.pageWidth > 630) {
        series.columns.template.maxWidth = 5
      } else {
        series.columns.template.maxWidth = 3
      }
      series.columns.template.stroke = am4core.color("#008BD0")

      series.columns.template.tooltipHTML = `
        <div class="future-{future}">
          <span class="tooltip-future">予測賃料合計</span>
          <span class="tooltip-past">過去賃料合計</span>
        </div>
        {short_income}万円
      `
      series.tooltip.pointerOrientation = 'vertical'
      this.initBullet(series)
    },
    initBullet(series) {
      let labelBullet = series.bullets.push(new am4charts.LabelBullet())
      labelBullet.label.html = `
      <div id="tooltip-year-wrapper">
        <div id="tooltip-year">
          ${this.$t("property_page.current")}
        </div>
        <div id="tooltip-arrow"></div>
      </div>`
      labelBullet.propertyFields.disabled = "hide_tooltip"
      labelBullet.label.dy = -23
      labelBullet.label.dx = 22
    }
  }
}
</script>

<style lang="scss" scoped>
.annual-income {
  h1 {
    color: #3d4f57;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0;
    margin: 4% 0 2% 0;
  }

  .chart-wrapper {
    background: #fff;
    border: 1px solid #dfe6ee;
    border-radius: 2px;
    height: 370px;
    overflow: hidden;
    padding: 20px;
  }
}
</style>

<style lang="scss">
.tooltip-future {
  display: none;
}

.tooltip-past {
  display: none;
}

.future-true {
  .tooltip-future {
    display: inline;
  }
}

.future-false {
  .tooltip-past {
    display: inline;
  }
}

#tooltip-year-wrapper {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4px 0;

  #tooltip-year {
    background-color: #fff;
    border: 1px solid #eb856b;
    border-radius: 4px;
    color: #eb856b;
    font-size: 0.8rem;
    padding: 3px 10px;
    text-align: center;
  }

  #tooltip-arrow {
    background-color: #fff;
    border-bottom: 1px solid #eb856b;
    border-right: 1px solid #eb856b;
    height: 6px;
    left: 20px;
    margin-top: -3px;
    transform: rotate(45deg);
    width: 6px;
    z-index: 20;
  }
}
</style>
